import { useState, useId } from 'react';
import { Collapse, ListItem, Typography, Paper, ButtonBase, useTheme, CircularProgress, Button } from '@mui/material';
import ListItemText from '@mui/material/ListItemText';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import WarningIcon from '@mui/icons-material/Warning';
import { useTranslation } from 'react-i18next';
import { useFeatureSupported } from '../../../../FeatureFlagsContext';

export default function ErrorDetailsOverlay(props: any) {
    const { cargoResponse, handleReupload, handleRexport } = props;
    const [isExpanded, setIsExpanded] = useState<boolean>(true);
    const [reuploadLoading, setReuploadLoading] = useState(false);
    const [rexportLoading, setRexportLoading] = useState(false);
    const { t } = useTranslation();
    const collapseId = useId();
    const theme = useTheme();
    const recallSupported = useFeatureSupported ? useFeatureSupported('Recall') : false;

    const handleToggle = () => {
        setIsExpanded(!isExpanded);
    };

    const warningBackgroundColor = 'var(--spectre-warning-surface)';
    const warningBorderColor = 'var(--spectre-warning-border)';
    const warningTextColor = theme.palette.warning.dark;
    const warningHeaderTextColor = theme.palette.warning.contrastText;

    const handleReuploadClick = async () => {
        if (!cargoResponse || !handleReupload) return;
        setReuploadLoading(true);
        try {
            await handleReupload(cargoResponse);
        } finally {
            setReuploadLoading(false);
        }
    };
    const handleRexportClick = async () => {
        if (!cargoResponse || !handleRexport) return;
        setRexportLoading(true);
        try {
            await handleRexport(cargoResponse);
        } finally {
            setRexportLoading(false);
        }
    };

    if (cargoResponse && ((cargoResponse.dimensionCode !== "0" || (cargoResponse.uploadCode !== "" && cargoResponse.uploadCode !== "0") || (cargoResponse.exportCode !== "" && cargoResponse.exportCode !== "0")))) {
        return (
            <Paper 
                elevation={6}
                sx={{
                    position: 'absolute',
                    top: 20,
                    right: 55,
                    maxWidth: 400,
                    zIndex: 1300,
                    backgroundColor: warningBackgroundColor,
                    backgroundImage: 'none',
                    border: `2px solid ${warningBorderColor}`,
                    borderRadius: 2,
                    overflow: 'hidden'
                }}
            >
                {/* Header - Always Visible */}
                <ButtonBase
                    onClick={handleToggle}
                    aria-expanded={isExpanded}
                    aria-controls={collapseId}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px 16px',
                        cursor: 'pointer',
                        backgroundColor: warningBackgroundColor,
                        width: '100%',
                        justifyContent: 'flex-start',
                    }}
                >
                    <WarningIcon sx={{ color: warningHeaderTextColor, mr: 1 }} />
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            color: warningHeaderTextColor, 
                            fontWeight: 600,
                            flexGrow: 1,
                            textAlign: 'left',
                        }}
                    >
                        {t('control.centerControl.errorDetails.title')}
                    </Typography>
                    {isExpanded
                        ? <ExpandLess sx={{ color: warningHeaderTextColor }} />
                        : <ExpandMore sx={{ color: warningHeaderTextColor }} />}
                </ButtonBase>

                {/* Collapsible Content */}
                <Collapse id={collapseId} in={isExpanded} timeout="auto" unmountOnExit>
                    <div style={{ padding: '0 16px 16px 16px' }}>
                                        {
                                            cargoResponse.dimensionErrors.map((error: { code: string, description: string }) => (
                                                <ListItem key={error.code} sx={{ py: 0.5, px: 0 }}>
                                                    <ListItemText>
                                                        <Typography display="inline" sx={{ fontWeight: 500, wordWrap: 'break-word', color: warningTextColor, whiteSpace: 'pre-line' }}>
                                                            <span style={{ fontWeight: 'bold' }}>{error.code}</span>: {error.description}
                                                        </Typography>
                                                    </ListItemText>
                                                </ListItem>
                                            ))
                                        }
                                        {
                                            cargoResponse.dimensionCode !== "0" && cargoResponse.dimensionCodeDescription !== "" ?
                                                <ListItem sx={{ backgroundColor: warningBackgroundColor }}>
                                                    <ListItemText>
                                                        <Typography display="inline" sx={{ fontWeight: 500, wordWrap: "break-word", color: warningTextColor }}>
                                                            <span style={{ fontWeight: 'bold' }}>{t('control.centerControl.errorDetails.dimension')}</span>: {cargoResponse.dimensionCodeDescription}
                                                        </Typography>
                                                    </ListItemText>
                                                </ListItem>
                                                :
                                                ""
                                        }
                                        {/* Upload error and button */}
                                        {cargoResponse.uploadCode !== "0" && cargoResponse.uploadCodeDescription !== "" && (
                                            <ListItem sx={{ backgroundColor: warningBackgroundColor, flexDirection: 'column', alignItems: 'flex-start' }}>
                                                <ListItemText>
                                                    <Typography display="inline" sx={{ fontWeight: 500, wordWrap: "break-word", color: warningTextColor }}>
                                                        <span style={{ fontWeight: 'bold' }}>{t('control.centerControl.errorDetails.upload')}</span>: {cargoResponse.uploadCodeDescription}
                                                    </Typography>
                                                </ListItemText>
                                                {recallSupported && (
                                                <Button
                                                    className="Upload-Export-Blue-Small"
                                                    sx={{ mt: 1 }}
                                                    onClick={handleReuploadClick}
                                                    disabled={reuploadLoading || cargoResponse.uploadCode === "0"}
                                                    size="small"
                                                >
                                                    {reuploadLoading ? <CircularProgress size={16} sx={{ color: '#FFFFFF', verticalAlign: 'middle' }} /> : (t('control.centerControl.centerBarcodeFieldButtons.reupload') || 'Re-upload')}
                                                </Button>
                                                    )}
                                            </ListItem>
                                        )}
                                        {/* Export error and button */}
                                        {cargoResponse.exportCode !== "0" && cargoResponse.exportCodeDescription !== "" && (
                                            <ListItem sx={{ backgroundColor: warningBackgroundColor, flexDirection: 'column', alignItems: 'flex-start' }}>
                                                <ListItemText>
                                                    <Typography display="inline" sx={{ fontWeight: 500, wordWrap: "break-word", color: warningTextColor }}>
                                                        <span style={{ fontWeight: 'bold' }}>{t('control.centerControl.errorDetails.export')}</span>: {cargoResponse.exportCodeDescription}
                                                    </Typography>
                                                </ListItemText>
                                                {recallSupported && (
                                                <Button
                                                    className="Upload-Export-Blue-Small"
                                                    sx={{ mt: 1 }}
                                                    onClick={handleRexportClick}
                                                    disabled={rexportLoading}
                                                    size="small"
                                                >
                                                    {rexportLoading ? <CircularProgress size={16} sx={{ color: '#FFFFFF', verticalAlign: 'middle' }} /> : (t('control.centerControl.centerBarcodeFieldButtons.reexport') || 'Re-export')}
                                                </Button>
                                                )}
                                            </ListItem>
                                        )}
                    </div>
                </Collapse>
            </Paper>
        )
    } else {
        return <></>
    }
}